// src/hooks/useNativePush.js
// Native push notifications for the installed Android/iOS app via Firebase Cloud
// Messaging (Capacitor Push Notifications plugin). This is the app-only path —
// the browser/PWA uses Web Push (see usePush.js). Guarded by isNativeApp() so it
// never runs (or throws) on the web build.
import { useEffect } from 'react';
import { isNativeApp } from '../utils/platform';
import api from '../utils/api';

export function useNativePush(currentUser) {
  useEffect(() => {
    if (!currentUser || !isNativeApp()) return;
    let cleanup = () => {};

    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        let perm = await PushNotifications.checkPermissions();
        if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive !== 'granted') {
          try { localStorage.setItem('tt_fcm_state', 'permission-denied'); } catch {}
          return;
        }

        // Registers with FCM and fires the 'registration' event with the token.
        await PushNotifications.register();

        const regH = await PushNotifications.addListener('registration', (token) => {
          api.post('/push/fcm-register', { token: token.value, platform: 'android' })
            .then(() => { try { localStorage.setItem('tt_fcm_state', 'registered'); } catch {} })
            .catch((e) => { try { localStorage.setItem('tt_fcm_state', `server-error: ${e?.response?.status || e?.message || 'failed'}`); } catch {} });
        });
        const errH = await PushNotifications.addListener('registrationError', (e) => {
          console.log('[NativePush] registration error', e?.error);
          try { localStorage.setItem('tt_fcm_state', `fcm-error: ${e?.error || 'unknown'}`); } catch {}
        });
        // Tapping a notification deep-links to the page it carried.
        const tapH = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const url = action?.notification?.data?.url;
          if (url) window.location.href = url;
        });

        cleanup = () => { regH.remove(); errH.remove(); tapH.remove(); };
      } catch (e) {
        console.log('[NativePush] not available:', e?.message);
      }
    })();

    return () => cleanup();
  }, [currentUser?._id]); // eslint-disable-line
}
